/**
 * ReviewFlow Frontend — Read More + Masonry Engine
 *
 * Read More Visibility:
 *   Buttons are rendered by PHP but are REMOVED FROM DOM when the text
 *   does not overflow its CSS-clamped container.  Detection uses real
 *   DOM measurements (scrollHeight > clientHeight), never character
 *   counts.  Checks run after fonts are loaded and after every layout
 *   change (resize, Elementor live update) via ResizeObserver.
 *
 * Masonry:
 *   CSS Grid + JS gridRowEnd span calculation driven by ResizeObserver
 *   and transitionend.  Never by aria-expanded attribute mutations.
 */
(function () {
	'use strict';

	// ─────────────────────────────────────────────────────────────────
	// Utility: debounce
	// ─────────────────────────────────────────────────────────────────

	function debounce(fn, wait) {
		var t = null;
		return function () {
			clearTimeout(t);
			t = setTimeout(fn, wait);
		};
	}

	/**
	 * Returns true if Masonry V2 engine should be used.
	 * Mirrors the feature-flag check in masonry-v2.js.
	 * Override: set window.reviewFlow = { experimental: { masonryV2: false } }
	 * before this script loads to force the legacy engine.
	 */
	function useV2Masonry() {
		var config = (window.reviewFlow && window.reviewFlow.experimental) || {};
		return config.masonryV2 !== false;
	}

	// ─────────────────────────────────────────────────────────────────
	// Utility: run after document fonts are ready
	// ─────────────────────────────────────────────────────────────────

	function afterFonts(fn) {
		if (typeof document.fonts !== 'undefined' && typeof document.fonts.ready !== 'undefined') {
			document.fonts.ready.then(fn);
		} else if (document.readyState === 'complete') {
			fn();
		} else {
			window.addEventListener('load', fn, { once: true });
		}
	}

	// ─────────────────────────────────────────────────────────────────
	// ReadMoreManager
	//
	// Owns the visibility of every Read More button.
	// Single source of truth: button is in DOM ↔ text overflows.
	// ─────────────────────────────────────────────────────────────────

	/**
	 * Registry: textElement → { button, clampedHeight }
	 * WeakMap so entries are GC-d with their elements.
	 * @type {WeakMap<Element, {button: Element, clampedHeight: number}>}
	 */
	var textRegistry = new WeakMap();

	/**
	 * Iterable set of all tracked text elements (for resize / full recheck).
	 * @type {Set<Element>}
	 */
	var allTexts = new Set();

	/** Pending measurements — dirty set for the RAF batch. */
	var dirtyTexts = new Set();
	var flushRafId = null;

	/**
	 * Queue an overflow check for `textEl` on the next double-RAF.
	 * Double-RAF ensures CSS (line-clamp, web fonts, layout) is fully
	 * computed before we read measurements.
	 */
	function scheduleCheck(textEl) {
		dirtyTexts.add(textEl);
		if (flushRafId !== null) {
			return; // already scheduled
		}
		flushRafId = requestAnimationFrame(function () {
			flushRafId = requestAnimationFrame(flush);
		});
	}

	/**
	 * Flush all pending overflow checks.
	 *
	 * Phase 1 — Read (no DOM writes, no style changes):
	 *   Measure scrollHeight and clientHeight for every dirty text element.
	 *
	 * Phase 2 — Write (DOM mutations + maxHeight):
	 *   Add or remove buttons, set maxHeight baseline.
	 */
	function flush() {
		flushRafId = null;

		var batch = Array.from(dirtyTexts);
		dirtyTexts.clear();

		// ── Phase 1: Read ─────────────────────────────────────────
		var results = [];
		for (var i = 0; i < batch.length; i++) {
			var el  = batch[i];
			var rec = textRegistry.get(el);
			if (!rec) {
				continue;
			}
			var expanded  = rec.button.getAttribute('aria-expanded') === 'true';
			// When expanded, always treat as overflowing so we never remove a
			// visible button mid-interaction.
			var overflows = expanded || (el.scrollHeight > el.clientHeight + 2);
			results.push({
				el:       el,
				rec:      rec,
				overflows: overflows,
				expanded:  expanded
			});
		}

		// ── Phase 2: Write ────────────────────────────────────────
		for (var j = 0; j < results.length; j++) {
			applyButtonPresence(results[j]);
		}
	}

	/**
	 * Add or remove the button from the DOM based on measured overflow.
	 * Sets the maxHeight baseline required for the expand/collapse animation.
	 */
	function applyButtonPresence(m) {
		var el       = m.el;
		var rec      = m.rec;
		var button   = rec.button;
		var inDom    = button.isConnected !== undefined
			? button.isConnected
			: document.documentElement.contains(button);
		var changed  = false;

		if (m.overflows) {
			if (!inDom) {
				// Re-insert button at the end of the text wrapper
				var parent = el.parentNode;
				if (parent) {
					parent.appendChild(button);
					changed = true;
				}
			}
			if (!m.expanded) {
				// Update clamped height and maxHeight baseline.
				// Only do this when collapsed so clampedHeight is accurate.
				// At this point clientHeight = CSS-only constrained height
				// because maxHeight is either unset or already equals clientHeight.
				rec.clampedHeight = el.clientHeight;
				if (!el.style.maxHeight) {
					el.style.maxHeight = rec.clampedHeight + 'px';
				}
			} else {
				// Expanded — ensure maxHeight reflects full scrollHeight
				// in case layout width changed while text was open.
				el.style.maxHeight = el.scrollHeight + 'px';
			}
		} else {
			if (inDom) {
				// Text fully visible — remove button completely from DOM
				button.parentNode.removeChild(button);
			}
			// Clear inline maxHeight — text renders at natural CSS height
			el.style.maxHeight = '';
		}
	}

	/**
	 * Register a button for managed overflow detection.
	 * Idempotent — safe to call multiple times on the same button.
	 */
	function registerButton(button) {
		var text = getText(button);

		if (!text) {
			// No target element — remove the orphaned button from DOM
			if (button.parentNode) {
				button.parentNode.removeChild(button);
			}
			return;
		}

		if (textRegistry.has(text)) {
			// Already registered — just re-run the check (e.g. Elementor rerender)
			scheduleCheck(text);
			return;
		}

		textRegistry.set(text, { button: button, clampedHeight: 0 });
		allTexts.add(text);

		// Observe the text element for size changes:
		//   - window resize (text reflows to new column width)
		//   - Elementor live control changes (font size, gap, padding…)
		//   - masonry recalculation changing column width
		if (readMoreResizeObserver) {
			readMoreResizeObserver.observe(text, function () {
				scheduleCheck(text);
			});
		}

		scheduleCheck(text);
	}

	/**
	 * Re-check every registered text element.
	 * Called after fonts load, after window resize, after Elementor updates.
	 */
	function recheckAll() {
		allTexts.forEach(function (textEl) {
			scheduleCheck(textEl);
		});
	}

	/**
	 * Single shared ResizeObserver for all Read More text elements.
	 * Fires when a text element changes size (layout, font, column width).
	 */
	var readMoreResizeObserver = window.ReviewFlowObserverManager
		? window.ReviewFlowObserverManager.resize
		: null;

	// ─────────────────────────────────────────────────────────────────
	// Expand / Collapse interactions
	// ─────────────────────────────────────────────────────────────────

	var collapseTimers = new WeakMap();

	function expand(button) {
		var text    = getText(button);
		var wrapper = getWrapper(button);
		if (!text || !wrapper) {
			return;
		}

		var pending = collapseTimers.get(text);
		if (pending) {
			clearTimeout(pending.timer);
			text.removeEventListener('transitionend', pending.onEnd);
			collapseTimers.delete(text);
		}

		// Lock at current clamped height before opening (animation start)
		text.style.maxHeight = text.clientHeight + 'px';
		wrapper.classList.add('reviewflow-card__text--expanded');
		button.setAttribute('aria-expanded', 'true');
		button.textContent = button.getAttribute('data-show-less-label');

		void text.offsetHeight; // Force reflow

		// Animate to full content height
		requestAnimationFrame(function () {
			text.style.maxHeight = text.scrollHeight + 'px';
		});

		// Dispatch custom event for containers (like Carousel) to react
		try {
			button.dispatchEvent(new CustomEvent('reviewflow:expand', {
				bubbles: true,
				detail: { button: button, text: text }
			}));
		} catch (e) {}
	}

	function collapse(button, animate) {
		var text    = getText(button);
		var wrapper = getWrapper(button);
		if (!text || !wrapper) {
			return;
		}

		var pending = collapseTimers.get(text);
		if (pending) {
			clearTimeout(pending.timer);
			text.removeEventListener('transitionend', pending.onEnd);
			collapseTimers.delete(text);
		}

		button.setAttribute('aria-expanded', 'false');
		button.textContent = button.getAttribute('data-read-more-label');

		var rec          = textRegistry.get(text);
		var targetHeight = rec && rec.clampedHeight ? rec.clampedHeight : 0;

		if (animate === false) {
			var prevTransition = text.style.transition;
			text.style.transition = 'none';
			wrapper.classList.remove('reviewflow-card__text--expanded');
			if (targetHeight > 0) {
				text.style.maxHeight = targetHeight + 'px';
			} else {
				text.style.maxHeight = '';
				scheduleCheck(text);
			}
			void text.offsetHeight; // Force reflow
			text.style.transition = prevTransition;
		} else {
			// Ensure starting height is explicitly set to full scrollHeight before transitioning down
			text.style.maxHeight = text.scrollHeight + 'px';
			void text.offsetHeight; // Force reflow

			requestAnimationFrame(function () {
				if (targetHeight > 0) {
					text.style.maxHeight = targetHeight + 'px';
				} else {
					text.style.maxHeight = '';
					scheduleCheck(text);
				}
			});

			// Keep wrapper's expanded class UNTIL the max-height transition finishes.
			// This prevents -webkit-line-clamp from prematurely truncating the text
			// and snapping the card height abruptly.
			var finishCollapse = function () {
				text.removeEventListener('transitionend', onEnd);
				collapseTimers.delete(text);
				wrapper.classList.remove('reviewflow-card__text--expanded');
				if (targetHeight > 0) {
					text.style.maxHeight = targetHeight + 'px';
				} else {
					text.style.maxHeight = '';
					scheduleCheck(text);
				}
			};

			var onEnd = function (e) {
				if (e.target === text && e.propertyName === 'max-height') {
					finishCollapse();
				}
			};

			text.addEventListener('transitionend', onEnd);
			var timerId = setTimeout(finishCollapse, 350);
			collapseTimers.set(text, { timer: timerId, onEnd: onEnd });
		}

		// Dispatch custom event for containers (like Carousel) to react
		try {
			button.dispatchEvent(new CustomEvent('reviewflow:collapse', {
				bubbles: true,
				detail: { button: button, text: text, animate: animate !== false }
			}));
		} catch (e) {}
	}

	/**
	 * Collapse all expanded reviews within container (or entire document).
	 *
	 * @param {Element|Document} [container]
	 * @param {boolean} [animate=true]
	 */
	function collapseAll(container, animate) {
		var scope = container || document;
		var buttons = scope.querySelectorAll('[data-reviewflow-read-more][aria-expanded="true"]');
		buttons.forEach(function (button) {
			collapse(button, animate);
		});
	}

	// ─────────────────────────────────────────────────────────────────
	// Click delegation (single listener on document)
	// ─────────────────────────────────────────────────────────────────

	function handleToggle(event) {
		var button = event.target.closest('[data-reviewflow-read-more]');
		if (!button) {
			return;
		}

		event.preventDefault();

		if (button.getAttribute('aria-expanded') === 'true') {
			collapse(button, true);
		} else {
			expand(button);
		}
	}

	// ─────────────────────────────────────────────────────────────────
	// Legacy Masonry Engine (CSS Grid + JS span calculation)
	// ─────────────────────────────────────────────────────────────────

	var masonryState = new WeakMap();
	var sharedResizeObserver = window.ReviewFlowObserverManager
		? window.ReviewFlowObserverManager.resize
		: null;
	var sharedMutationObserver = window.ReviewFlowObserverManager
		? window.ReviewFlowObserverManager.mutation
		: null;

	function initMasonry(root) {
		var scope = root || document;
		scope.querySelectorAll('.reviewflow--layout-masonry .reviewflow__grid--layout-masonry').forEach(setupMasonryGrid);
	}

	function setupMasonryGrid(grid) {
		var state = masonryState.get(grid);
		if (state) {
			scheduleMasonryLayout(grid, state);
			return;
		}

		state = {
			pending:          false,
			rafId:            null,   // pending RAF id for cancelAnimationFrame on destroy
			rowHeight:        8,
			rowGap:           0,
			resizeObserver:   null,
			mutationObserver: null,
			observedItems:    [],     // items tracked in the resize observer pool
			mutationCallback: null,  // callback ref for shared mutation pool unobserve
			transListeners:   [],    // { el, fn } pairs for transitionend cleanup
			imgListeners:     []     // { img, fn } pairs for img load cleanup
		};

		masonryState.set(grid, state);
		updateMasonryMetrics(grid, state);
		scheduleMasonryLayout(grid, state);

		if (sharedResizeObserver) {
			state.resizeObserver = sharedResizeObserver;
			grid.querySelectorAll('.reviewflow-masonry-item').forEach(function (item) {
				var cb = function () {
					updateMasonryMetrics(grid, state);
					scheduleMasonryLayout(grid, state);
				};
				state.resizeObserver.observe(item, cb);
				state.observedItems.push(item);
			});
		} else if (typeof window.ResizeObserver === 'function') {
			state.resizeObserver = new ResizeObserver(function () {
				updateMasonryMetrics(grid, state);
				scheduleMasonryLayout(grid, state);
			});
			grid.querySelectorAll('.reviewflow-masonry-item').forEach(function (item) {
				state.resizeObserver.observe(item);
			});
		}

		if (sharedMutationObserver) {
			state.mutationObserver = sharedMutationObserver;
			// Store the callback reference so it can be removed from the shared pool on destroy.
			state.mutationCallback = function () {
				if (state.resizeObserver === sharedResizeObserver) {
					grid.querySelectorAll('.reviewflow-masonry-item').forEach(function (item) {
						if (state.observedItems.indexOf(item) === -1) {
							var cb = function () {
								updateMasonryMetrics(grid, state);
								scheduleMasonryLayout(grid, state);
							};
							state.resizeObserver.observe(item, cb);
							state.observedItems.push(item);
						}
					});
				} else if (state.resizeObserver) {
					grid.querySelectorAll('.reviewflow-masonry-item').forEach(function (item) {
						state.resizeObserver.observe(item);
					});
				}
				updateMasonryMetrics(grid, state);
				scheduleMasonryLayout(grid, state);
			};
			state.mutationObserver.observe(grid, state.mutationCallback, { childList: true, subtree: false });
		} else {
			state.mutationObserver = new MutationObserver(function () {
				if (state.resizeObserver) {
					grid.querySelectorAll('.reviewflow-masonry-item').forEach(function (item) {
						state.resizeObserver.observe(item);
					});
				}
				updateMasonryMetrics(grid, state);
				scheduleMasonryLayout(grid, state);
			});

			// IMPORTANT: Do NOT observe 'aria-expanded' or 'class' attribute changes.
			// Those fire mid-transition and would trigger relayout at an incorrect
			// intermediate card height, corrupting the grid.
			// Relayout after expand/collapse is driven exclusively by transitionend.
			state.mutationObserver.observe(grid, {
				childList: true,
				subtree:   false
			});
		}

		grid.querySelectorAll('img').forEach(function (img) {
			var fn = function () {
				updateMasonryMetrics(grid, state);
				scheduleMasonryLayout(grid, state);
			};
			img.addEventListener('load', fn);
			state.imgListeners.push({ img: img, fn: fn });
		});

		var texts = grid.querySelectorAll('.reviewflow-card__text-content');
		texts.forEach(function (el) {
			if (el.__rfLegacyBound) {
				return;
			}
			el.__rfLegacyBound = true;
			var fn = function (event) {
				if (event.propertyName && event.propertyName !== 'max-height') {
					return;
				}
				updateMasonryMetrics(grid, state);
				scheduleMasonryLayout(grid, state);
			};
			el.addEventListener('transitionend', fn);
			state.transListeners.push({ el: el, fn: fn });
		});
	}

	function updateMasonryMetrics(grid, state) {
		var styles    = window.getComputedStyle(grid);
		var rowHeight = parseFloat(styles.getPropertyValue('--rf-masonry-row-height'));
		var rowGap    = parseFloat(styles.rowGap || styles.getPropertyValue('row-gap'));

		state.rowHeight = isNaN(rowHeight) ? 8  : rowHeight;
		state.rowGap    = isNaN(rowGap)    ? 0  : rowGap;
	}

	function scheduleMasonryLayout(grid, state) {
		if (state.pending) {
			return;
		}
		state.pending = true;
		state.rafId = requestAnimationFrame(function () {
			state.rafId = null;
			state.pending = false;
			layoutMasonryGrid(grid, state);
		});
	}

	function layoutMasonryGrid(grid, state) {
		// Phase 1: Read all heights
		var items = grid.querySelectorAll('.reviewflow-masonry-item');
		var heights = [];
		items.forEach(function (item) {
			var card = item.querySelector('.reviewflow-card');
			heights.push(card ? card.getBoundingClientRect().height : 0);
		});

		// Phase 2: Write all spans
		items.forEach(function (item, idx) {
			var height = heights[idx];
			if (!height) {
				return;
			}
			var span = Math.ceil((height + state.rowGap) / (state.rowHeight + state.rowGap));
			item.style.gridRowEnd = 'span ' + Math.max(1, span);
		});
	}

	// ─────────────────────────────────────────────────────────────────
	// Helpers
	// ─────────────────────────────────────────────────────────────────

	function getWrapper(button) {
		return button.closest('.reviewflow-card__text');
	}

	function getText(button) {
		var id = button.getAttribute('aria-controls');
		return id ? document.getElementById(id) : null;
	}

	// ─────────────────────────────────────────────────────────────────
	// Init
	// ─────────────────────────────────────────────────────────────────

	/**
	 * Destroy all legacy masonry engines attached to grids inside `root`.
	 * Removes all observers, RAF callbacks, and event listeners created by
	 * setupMasonryGrid().  Called by MasonryLegacyEngine.destroy(root) in
	 * layout-engine-manager.js.
	 *
	 * @param {Element|Document} [root]
	 */
	function destroyMasonry(root) {
		var scope = root || document;
		scope.querySelectorAll('.reviewflow__grid--layout-masonry').forEach(function (grid) {
			var state = masonryState.get(grid);
			if (!state) { return; }

			// Cancel any pending layout RAF
			if (state.rafId) {
				cancelAnimationFrame(state.rafId);
				state.rafId = null;
			}
			state.pending = false;

			// Unobserve resize items
			if (state.resizeObserver) {
				if (state.resizeObserver === sharedResizeObserver) {
					// Shared pool — unobserve each tracked item individually
					state.observedItems.forEach(function (item) {
						state.resizeObserver.unobserve(item);
					});
				} else {
					// Private native ResizeObserver — disconnect all at once
					state.resizeObserver.disconnect();
				}
				state.resizeObserver = null;
			}
			state.observedItems = [];

			// Unobserve mutation observer
			if (state.mutationObserver) {
				if (state.mutationObserver === sharedMutationObserver) {
					// Shared pool — pass stored callback so only our handler is removed
					state.mutationObserver.unobserve(grid, state.mutationCallback);
				} else {
					// Private native MutationObserver
					state.mutationObserver.disconnect();
				}
				state.mutationObserver = null;
				state.mutationCallback = null;
			}

			// Remove transitionend listeners and clear the bound flag so
			// elements can be re-bound if the grid is re-initialized later.
			state.transListeners.forEach(function (p) {
				p.el.removeEventListener('transitionend', p.fn);
				delete p.el.__rfLegacyBound;
			});
			state.transListeners = [];

			// Remove img load listeners
			state.imgListeners.forEach(function (p) {
				p.img.removeEventListener('load', p.fn);
			});
			state.imgListeners = [];

			masonryState.delete(grid);
		});
	}

	var globalListenersAttached = false;

	function init() {
		// Register every rendered Read More button for managed overflow detection
		document.querySelectorAll('[data-reviewflow-read-more]').forEach(registerButton);
		if (window.ReviewFlowLayoutEngineManager) {
			// Single engine: V2 unless explicitly disabled via feature flag.
			if (useV2Masonry()) {
				window.ReviewFlowLayoutEngineManager.create('masonry-v2', document);
			} else {
				// Legacy engine selected: strip the V2 CSS classes that the PHP
				// template always stamps onto the grid.  Without this, masonry-v2.css
				// rules would still apply to grids the legacy row-span engine manages,
				// causing visual conflicts (wrong row heights, double gap rules, etc.).
				document.querySelectorAll('.rf-masonry-v2').forEach(function (grid) {
					grid.classList.remove('rf-masonry-v2');
				});
				document.querySelectorAll('.rf-masonry-v2-item').forEach(function (item) {
					item.classList.remove('rf-masonry-v2-item');
				});
				window.ReviewFlowLayoutEngineManager.create('masonry-legacy', document);
			}
		} else {
			initMasonry();
		}

		if (!globalListenersAttached) {
			// Click handler via event delegation (works for re-inserted buttons too)
			document.addEventListener('click', handleToggle);

			// Re-check all overflow states on window resize.
			// Text reflows → some cards may gain or lose overflow.
			window.addEventListener('resize', debounce(function () {
				recheckAll();
			}, 150));

			globalListenersAttached = true;
		}
	}

	// ─────────────────────────────────────────────────────────────────
	// Bootstrap
	// ─────────────────────────────────────────────────────────────────

	function bootstrap() {
		init();

		// Run overflow checks AFTER fonts are loaded.
		// Fonts affect line-height and character widths, which affect
		// whether text wraps past the line-clamp threshold.
		afterFonts(function () {
			recheckAll();
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', bootstrap);
	} else {
		bootstrap();
	}

	// ─────────────────────────────────────────────────────────────────
	// Public API
	// ─────────────────────────────────────────────────────────────────

	/** Re-register any new buttons (e.g. after Elementor widget rerender). */
	window.reviewflowInitReadMore = init;

	/** Re-check all overflow states (e.g. after layout change). */
	window.reviewflowRefreshReadMore = recheckAll;

	/** Initialize masonry for all grids (or a specific root element). */
	window.reviewflowInitMasonry = initMasonry;

	/** Destroy all legacy masonry engines inside root. Called by MasonryLegacyEngine.destroy(). */
	window.reviewflowDestroyMasonry = destroyMasonry;

	/**
	 * Re-check overflow for all registered text elements.
	 * Call this after any layout change that might affect text wrapping:
	 *   ReviewFlowReadMore.recheck()
	 *   window.reviewflowRecheckReadMore()
	 */
	window.reviewflowRecheckReadMore = recheckAll;

	/** Collapse all expanded reviews within container (or entire document). */
	window.reviewflowCollapseAll = collapseAll;

	/** Expand a specific review button. */
	window.reviewflowExpand = expand;

	/** Collapse a specific review button. */
	window.reviewflowCollapse = collapse;

	/** Public namespace for Read More operations. */
	window.ReviewFlowReadMore = Object.assign(window.ReviewFlowReadMore || {}, {
		init: init,
		recheck: recheckAll,
		expand: expand,
		collapse: collapse,
		collapseAll: collapseAll
	});

}());
